import 'Components/atoms/vm-button/vm-button';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createInfiniteQueryController } from '@tanstack/lit-query';
import { commentsQueryOptions } from 'Api/comments';
import { queryClient } from 'Api/query-client';
import Styles from './vm-video-comments-button.scss';

/**
 * Mobile-only entry point into the video-page comments bottom sheet. Sits
 * inside `<vm-video-actions>` as a fourth pill alongside Thumbs up / Share /
 * Save. Hidden via CSS on `desktop-and-up`, where the comments organism
 * renders inline below the player and a dedicated trigger isn't needed.
 *
 * @attr video-slug - Slug used to look up the cached comment count.
 * @attr can-view-comments - Set when the user has `comment.view_comments`.
 *   Gates the query so a user without permission can't trigger a live fetch
 *   (which would 401) if the organism's SSR hydration is absent/malformed.
 *
 * @fires vm-video-comments-button:open - Bubbles + composed + cancelable.
 *   Detail: none. Cancelable so consumers can intercept; listeners that
 *   honor `event.defaultPrevented` skip opening the sheet.
 */
@customElement('vm-video-comments-button')
export class VmVideoCommentsButton extends LitElement {
    static override styles = Styles;

    @property({ type: String, attribute: 'video-slug' })
    videoSlug = '';

    @property({ type: Boolean, attribute: 'can-view-comments' })
    canViewComments = false;

    private _query = createInfiniteQueryController(
        this,
        () => ({
            ...commentsQueryOptions(this.videoSlug),
            enabled: !!this.videoSlug && this.canViewComments,
        }),
        queryClient,
    );

    private _onClick = (): void => {
        this.dispatchEvent(
            new CustomEvent('vm-video-comments-button:open', {
                bubbles: true,
                composed: true,
                cancelable: true,
            }),
        );
    };

    override render() {
        const count = this._query.current.data?.pages[0]?.total_count ?? 0;
        const label = count > 0 ? `${count} Comments` : 'Add a comment';
        return html`
            <vm-button variant="solid" color-scheme="secondary" size="sm" @click=${this._onClick}>
                <vm-icon-next slot="icon-leading" name="message-square-02" size="sm"></vm-icon-next>
                ${label}
            </vm-button>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-comments-button': VmVideoCommentsButton;
    }
    interface HTMLElementEventMap {
        'vm-video-comments-button:open': CustomEvent;
    }
}
